import {
  COLOR_PALETTE_20,
  AGE_GROUP_COLORS,
  GENDER_COLORS,
  FEATURE_COLORS,
  PROVINCE_COLORS,
  PRIMARY_BAR_COLOR,
  formatNumber,
  formatCategoryAxisLabel,
} from '../../constants';

export {
  COLOR_PALETTE_20,
  AGE_GROUP_COLORS,
  GENDER_COLORS,
  FEATURE_COLORS,
  PROVINCE_COLORS,
  PRIMARY_BAR_COLOR,
  formatNumber,
  formatCategoryAxisLabel,
};

export const getAccessUserChartTitle = (viewBy: string): string => {
  switch (viewBy) {
    case 'age':
      return 'Lượt truy cập theo độ tuổi';
    case 'gender':
      return 'Lượt truy cập theo giới tính';
    case 'province':
      return 'Lượt truy cập theo tỉnh thành';
    case 'feature':
      return 'Lượt truy cập theo chức năng hệ thống';
    case 'age_gender':
      return 'Lượt truy cập theo độ tuổi và giới tính';
    case 'age_province':
      return 'Lượt truy cập theo độ tuổi và tỉnh thành';
    case 'feature_gender':
      return 'Lượt truy cập theo chức năng hệ thống và giới tính';
    case 'feature_age':
      return 'Lượt truy cập theo chức năng hệ thống và độ tuổi';
    case 'feature_province':
      return 'Lượt truy cập theo chức năng hệ thống và tỉnh thành';
    case 'custom':
      return 'Danh sách tùy chỉnh';
    default:
      return 'Thống kê lượt truy cập';
  }
};

/**
 * 1. Donut Chart (for Gender)
 */
export const buildDonutChartOption = (
  items: { name: string; value: number; percentage?: number }[] = [],
  seriesName: string = 'Giới tính',
) => {
  return {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) =>
        `${params.name}: ${params.percent}% (${formatNumber(params.value)})`,
    },
    legend: {
      orient: 'horizontal',
      top: 0,
      icon: 'rect',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 24,
      textStyle: {
        fontSize: 13,
        color: '#262626',
      },
    },
    series: [
      {
        name: seriesName,
        type: 'pie',
        radius: ['55%', '82%'],
        center: ['50%', '52%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 0,
          borderColor: 'transparent',
          borderWidth: 0,
        },
        label: {
          show: true,
          position: 'inside',
          formatter: (params: any) =>
            params.value > 0
              ? `${params.percent}%\n(${formatNumber(params.value)})`
              : '',
          color: '#ffffff',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 16,
        },
        labelLine: {
          show: false,
        },
        emphasis: {
          disabled: true,
        },
        data: items.map((item) => ({
          name: item.name,
          value: item.value,
          itemStyle: {
            color: GENDER_COLORS[item.name] || '#bfbfbf',
          },
        })),
      },
    ],
  };
};

/**
 * 2. Vertical Bar Chart with Dual Labels (for Single Dimension: Age, Province, Feature)
 */
export const buildVerticalBarOption = (params: {
  categories: string[];
  values: number[];
  totalAccess?: number;
  seriesName?: string;
}) => {
  const { categories, values, totalAccess = 0, seriesName = 'Số lượt truy cập' } = params;
  const hasZoom = categories.length > 10;
  const total = totalAccess > 0 ? totalAccess : values.reduce((sum, v) => sum + (Number(v) || 0), 0);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (tooltipParams: any) => {
        const p = tooltipParams[0];
        const val = Number(p?.value) || 0;
        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
        return `<b>${p?.name || ''}</b><br/>${seriesName}: ${formatNumber(val)} (${pct}%)`;
      },
    },
    grid: {
      top: '12%',
      left: '3%',
      right: '4%',
      bottom: hasZoom ? 125 : 85,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        interval: 0,
        rotate: 0,
        fontSize: categories.length > 15 ? 10 : 11,
        lineHeight: categories.length > 15 ? 12 : 14,
        align: 'center',
        verticalAlign: 'top',
        formatter: (val: string) => formatCategoryAxisLabel(val, categories.length),
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: '#e8e8e8',
        },
      },
    },
    dataZoom: hasZoom
      ? [
          {
            type: 'slider',
            show: true,
            startValue: 0,
            endValue: 9,
            height: 18,
            bottom: 8,
            borderColor: 'transparent',
            backgroundColor: '#f5f5f5',
            fillerColor: 'rgba(24, 144, 255, 0.2)',
            handleStyle: {
              color: '#1890ff',
              borderColor: '#1890ff',
            },
          },
          { type: 'inside', startValue: 0, endValue: 9 },
        ]
      : [],
    series: [
      {
        name: seriesName,
        type: 'bar',
        barMaxWidth: 60,
        itemStyle: {
          color: PRIMARY_BAR_COLOR,
          borderRadius: [2, 2, 0, 0],
        },
        label: {
          show: true,
          position: 'inside',
          formatter: (p: any) => {
            const val = Number(p.value) || 0;
            const maxVal = Math.max(...values.map(Number), 1);
            if (val <= 0 || val / maxVal < 0.04) return '';
            return formatNumber(val);
          },
          fontSize: 11,
          fontWeight: 'bold',
          color: '#ffffff',
        },
        data: values,
      },
      {
        name: 'Tỷ lệ %',
        type: 'bar',
        barMaxWidth: 60,
        barGap: '-100%',
        itemStyle: {
          color: 'transparent',
        },
        tooltip: {
          show: false,
        },
        label: {
          show: true,
          position: 'top',
          formatter: (p: any) => {
            if (p.value === 0) return '';
            const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0;
            return `(${pct}%)`;
          },
          fontSize: 11,
          color: '#333333',
        },
        data: values,
      },
    ],
  };
};

/**
 * 3. Vertical Stacked Bar Chart (for Combined Views: Age & Gender, Age & Province, Feature & Gender, etc.)
 */
export const buildStackedBarOption = (params: {
  categories: string[];
  series: { name: string; data: number[] }[];
  totalAccess?: number;
}) => {
  const { categories, series } = params;
  const hasZoom = categories.length > 10;

  // Tính tổng giá trị của từng cột để xác định chiều cao cột tối đa trên trục Y
  const colTotals = categories.map((_, colIdx) =>
    series.reduce((sum, s) => sum + (Number(s.data[colIdx]) || 0), 0)
  );
  const maxColTotal = Math.max(...colTotals, 1);

  const legendRows: any[] = [];
  const itemsPerRow = series.length <= 8 ? 4 : 5;
  for (let i = 0; i < series.length; i += itemsPerRow) {
    legendRows.push({
      data: series.slice(i, i + itemsPerRow).map((s) => s.name),
      top: Math.floor(i / itemsPerRow) * 24,
      left: 'center',
      itemGap: 24,
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { fontSize: 12, color: '#333333' },
      icon: 'rect',
    });
  }

  const calculatedGridTop = Math.max(65, legendRows.length * 24 + 25);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (tooltipParams: any[]) => {
        let total = 0;
        tooltipParams.forEach((p) => (total += Number(p.value) || 0));
        let html = `<b>${tooltipParams[0]?.name || ''}</b> (Tổng: ${formatNumber(total)})<br/>`;
        tooltipParams.forEach((p) => {
          const val = Number(p.value) || 0;
          if (val > 0) {
            const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
            html += `${p.marker} ${p.seriesName}: <b>${formatNumber(val)}</b> (${pct}%)<br/>`;
          }
        });
        return html;
      },
    },
    legend: legendRows,
    grid: {
      top: calculatedGridTop,
      left: '3%',
      right: '4%',
      bottom: hasZoom ? 75 : 55,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        interval: 0,
        rotate: 0,
        fontSize: 11,
        lineHeight: 14,
        align: 'center',
        verticalAlign: 'top',
        color: '#262626',
        formatter: (val: string) => formatCategoryAxisLabel(val, categories.length),
      },
      axisTick: { show: false },
      axisLine: {
        lineStyle: {
          color: '#d9d9d9',
        },
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: '#e8e8e8',
        },
      },
      axisLabel: {
        formatter: (val: number) => formatNumber(val),
        color: '#595959',
        fontSize: 12,
      },
    },
    dataZoom: hasZoom
      ? [
          {
            type: 'slider',
            show: true,
            startValue: 0,
            endValue: 9,
            height: 12,
            bottom: 6,
            borderColor: 'transparent',
            backgroundColor: '#f0f0f0',
            fillerColor: '#bfbfbf',
            showDetail: false,
            handleSize: '100%',
            handleStyle: {
              color: '#8c8c8c',
              borderColor: '#8c8c8c',
            },
            moveHandleStyle: {
              color: '#8c8c8c',
            },
          },
          { type: 'inside', startValue: 0, endValue: 9 },
        ]
      : [],
    series: series.map((s, idx) => {
      let itemColor =
        FEATURE_COLORS[s.name] ||
        AGE_GROUP_COLORS[s.name] ||
        GENDER_COLORS[s.name] ||
        PROVINCE_COLORS[s.name];
      if (!itemColor) {
        itemColor = COLOR_PALETTE_20[idx % COLOR_PALETTE_20.length];
      }
      return {
        name: s.name,
        type: 'bar',
        stack: 'total',
        barMaxWidth: 50,
        itemStyle: { color: itemColor },
        label: {
          show: true,
          position: 'inside',
          align: 'center',
          verticalAlign: 'middle',
          formatter: (p: any) => {
            const val = Number(p.value) || 0;
            // Ẩn nhãn nếu giá trị = 0 hoặc ô stack quá nhỏ (< 4% chiều cao cột cao nhất) để tránh đè chữ
            if (val <= 0 || val / maxColTotal < 0.04) return '';
            return formatNumber(val);
          },
          fontSize: 11,
          color: '#fff',
        },
        data: s.data,
      };
    }),
  };
};

/**
 * Main builder dispatcher: Auto-detects chart type and returns the corresponding ECharts option
 */
export const buildAccessUserChartOption = (chartData: any) => {
  if (!chartData) return {};
  if (chartData.viewBy === 'gender' || chartData.items) {
    return buildDonutChartOption(chartData.items || []);
  }
  if (chartData.series && chartData.series.length > 0) {
    return buildStackedBarOption({
      categories: chartData.categories || [],
      series: chartData.series,
      totalAccess: chartData.totalAccess,
    });
  }
  return buildVerticalBarOption({
    categories: chartData.categories || [],
    values: chartData.values || [],
    totalAccess: chartData.totalAccess,
    seriesName: 'Số lượt truy cập',
  });
};

/**
 * Frequency sub-tab chart builder
 */
export const buildAccessFrequencyChartOption = (chartData: any) => {
  if (!chartData) return {};
  if (chartData.viewBy === 'frequency_return_users') {
    const categories = chartData.categories || [];
    const values = chartData.values || [];
    const hasZoom = categories.length > 25;

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => {
          if (!params || params.length === 0) return '';
          const p = params[0];
          return `<b>${p.name}</b><br/>${p.marker} ${p.seriesName}: <b>${formatNumber(p.value)}</b>`;
        },
      },
      legend: {
        show: true,
        top: 0,
        left: 'center',
        icon: 'rect',
        itemWidth: 20,
        itemHeight: 2,
        textStyle: {
          fontSize: 12,
          color: '#595959',
        },
        data: ['Số người dùng truy cập lại hệ thống'],
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: hasZoom ? 55 : '8%',
        top: 40,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories,
        axisLabel: {
          fontSize: 11,
          formatter: (val: string) => {
            if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
              const [y, m, d] = val.split('-');
              return `${d}-${m}-${y}`;
            }
            return val;
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#e8e8e8',
          },
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: {
          formatter: (val: number) => formatNumber(val),
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#e8e8e8',
          },
        },
      },
      dataZoom: hasZoom
        ? [
            {
              type: 'slider',
              show: true,
              startValue: 0,
              endValue: 9,
              height: 18,
              bottom: 8,
              borderColor: 'transparent',
              backgroundColor: '#f5f5f5',
              fillerColor: 'rgba(24, 144, 255, 0.2)',
              handleStyle: {
                color: '#1890ff',
                borderColor: '#1890ff',
              },
            },
            { type: 'inside', startValue: 0, endValue: 9 },
          ]
        : [],
      series: [
        {
          name: 'Số người dùng truy cập lại hệ thống',
          type: 'line',
          smooth: false,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: '#1890ff' },
          lineStyle: { width: 2, color: '#1890ff' },
          label: {
            show: true,
            position: 'top',
            formatter: (p: any) => (p.value > 0 ? formatNumber(p.value) : ''),
            fontSize: 11,
            color: '#595959',
          },
          data: values,
        },
      ],
    };
  }
  return buildVerticalBarOption({
    categories: chartData.categories || [],
    values: chartData.values || [],
    totalAccess: chartData.totalAccess,
    seriesName: chartData.viewBy === 'frequency_times' ? 'Số người dùng' : 'Số lượt truy cập',
  });
};

